import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, Users, FileText, Euro, Target,
  BarChart3, PieChart, ArrowRight, Building2, Clock, Percent,
  ChevronDown, AlertCircle
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from 'recharts';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';

// Period selector options
const PERIOD_OPTIONS = [
  { value: 1, label: '1 mese' },
  { value: 3, label: '3 mesi' },
  { value: 6, label: '6 mesi' },
  { value: 12, label: 'Anno' }
];

// KPI Card strategico
const StrategicKPICard = ({ title, value, change, icon: Icon, formatValue, color = '#0078C8' }) => {
  const formattedValue = formatValue ? formatValue(value) : value;
  const hasChange = change !== null && change !== undefined;
  const isPositive = change >= 0;
  
  return (
    <Card className="card-abitalo">
      <CardContent className="pt-4 pb-4 px-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#737373] uppercase tracking-wide font-medium">{title}</span>
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center" 
            style={{ backgroundColor: `${color}15` }}
          >
            <Icon className="w-4 h-4" style={{ color }} />
          </div>
        </div>
        <div className="text-2xl font-bold" style={{ color }}>{formattedValue}</div>
        {hasChange && (
          <div className={`flex items-center gap-1 mt-1 text-xs ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
            {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{isPositive ? '+' : ''}{change}% vs periodo prec.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Funnel Step con drop-off
const FunnelStep = ({ step, maxCount, isFirst }) => {
  const width = maxCount > 0 ? Math.max((step.count / maxCount) * 100, 8) : 8;
  
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-28 flex-shrink-0">
        <span className="text-sm font-medium text-[#3C3C3C]">{step.stage_it}</span>
      </div>
      <div className="flex-1 flex items-center">
        <div className="flex-1 h-7 bg-gray-100 rounded-md overflow-hidden relative">
          <div 
            className="h-full rounded-md transition-all duration-500 flex items-center px-2.5"
            style={{ width: `${width}%`, backgroundColor: step.color }}
          >
            <span className="text-white text-sm font-bold whitespace-nowrap">{step.count}</span>
          </div>
        </div>
      </div>
      <div className="w-16 text-right">
        <span className="text-sm font-semibold text-[#0078C8]">{step.rate}%</span>
      </div>
      <div className="w-16 text-right">
        {!isFirst ? (
          <span className={`text-xs font-medium ${step.dropoff < 0 ? 'text-red-500' : 'text-gray-400'}`}>
            {step.dropoff !== 0 ? `${step.dropoff}%` : '-'}
          </span>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </div>
    </div>
  );
};

// Project Status Compact Item
const ProjectStatusItem = ({ status, count, color }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
    <div className="flex items-center gap-2">
      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-sm text-[#3C3C3C]">{status}</span>
    </div>
    <span className="text-sm font-semibold text-[#3C3C3C]">{count}</span>
  </div>
);

const AnalyticsPage = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(6);
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [leadsTrend, setLeadsTrend] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [projectsStatus, setProjectsStatus] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState(null);

  // Check authorization
  useEffect(() => {
    if (!isAdmin && user?.role !== 'project_manager') {
      navigate('/');
      toast.error('Accesso non autorizzato');
    }
  }, [isAdmin, user, navigate]);

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, funnelRes, trendRes, revenueRes, projectsRes, teamRes] = await Promise.all([
        api.get(`/analytics/summary?months=${period}`),
        api.get(`/analytics/conversion-funnel?months=${period}`),
        api.get(`/analytics/leads/trend?months=${period}`),
        api.get(`/analytics/revenue?months=${period}`),
        api.get('/analytics/projects/status'),
        api.get(`/analytics/team/performance?months=${period}&role_filter=${roleFilter}`)
      ]);
      
      setSummary(summaryRes.data);
      setFunnel(funnelRes.data);
      setLeadsTrend(trendRes.data);
      setRevenue(revenueRes.data);
      setProjectsStatus(projectsRes.data);
      setTeamPerformance(teamRes.data);
    } catch (err) {
      console.error('Error loading analytics:', err);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [period, roleFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload team performance when role filter changes
  const handleRoleFilterChange = async (newRole) => {
    setRoleFilter(newRole);
    try {
      const res = await api.get(`/analytics/team/performance?months=${period}&role_filter=${newRole}`);
      setTeamPerformance(res.data);
    } catch (err) {
      console.error('Error loading team performance:', err);
    }
  };

  if (!isAdmin && user?.role !== 'project_manager') return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="analytics-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF7819] mx-auto mb-3"></div>
          <p className="text-[#737373]">Caricamento analytics...</p>
        </div>
      </div>
    );
  }

  const kpis = summary?.kpis || {};
  const funnelData = funnel?.funnel || [];
  const maxFunnelCount = funnelData.length > 0 ? funnelData[0].count : 1;
  const trendData = leadsTrend?.trend || [];
  const revenueData = revenue?.monthly || [];
  const projectsDist = projectsStatus?.distribution || [];
  const teamData = teamPerformance?.team || [];

  return (
    <div className="space-y-5 animate-fade-in" data-testid="analytics-page">
      {/* Header with Period Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#FF7819] to-[#FF9A4D] rounded-xl flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#3C3C3C]">Analytics</h1>
            <p className="text-xs text-[#737373]">Analisi strategica e performance nel tempo</p>
          </div>
        </div>
        
        {/* Period Filter */}
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                period === opt.value 
                  ? 'bg-white text-[#0078C8] shadow-sm' 
                  : 'text-[#737373] hover:text-[#3C3C3C]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ROW 1: KPI Strategici */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-strategic-section">
        <StrategicKPICard 
          title="Lead Acquisiti"
          value={kpis.leads_acquired?.value || 0}
          change={kpis.leads_acquired?.change}
          icon={Users}
          color="#0078C8"
        />
        <StrategicKPICard 
          title="Contratti Firmati"
          value={kpis.contracts_signed?.value || 0}
          change={kpis.contracts_signed?.change}
          icon={FileText}
          color="#008A3D"
        />
        <StrategicKPICard 
          title="Valore Contratti"
          value={kpis.contracts_value?.value || 0}
          change={kpis.contracts_value?.change}
          icon={Euro}
          formatValue={formatCurrency}
          color="#FF7819"
        />
        <StrategicKPICard 
          title="Ticket Medio"
          value={kpis.ticket_medio?.value || 0}
          change={kpis.ticket_medio?.change}
          icon={Target}
          formatValue={(v) => v > 0 ? formatCurrency(v) : 'N/D'}
          color="#9333EA"
        />
        <StrategicKPICard 
          title="Conversione"
          value={kpis.conversion_rate?.value || 0}
          change={kpis.conversion_rate?.change}
          icon={Percent}
          formatValue={(v) => `${v}%`}
          color="#EC4899"
        />
      </div>

      {/* ROW 2: Funnel Conversione */}
      <Card className="card-abitalo" data-testid="funnel-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#0078C8]" />
              <span style={{ letterSpacing: '0.01em' }}>Funnel di Conversione</span>
            </CardTitle>
            <span className="text-xs text-[#737373]">
              Tasso conversione: <strong className="text-[#008A3D]">{funnel?.overall_conversion_rate || 0}%</strong>
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {/* Header row */}
          <div className="flex items-center gap-3 mb-2 pb-2 border-b border-gray-100">
            <div className="w-28 flex-shrink-0">
              <span className="text-xs font-medium text-[#737373] uppercase">Stage</span>
            </div>
            <div className="flex-1">
              <span className="text-xs font-medium text-[#737373] uppercase">Progressione</span>
            </div>
            <div className="w-16 text-right">
              <span className="text-xs font-medium text-[#737373] uppercase">% Tot</span>
            </div>
            <div className="w-16 text-right">
              <span className="text-xs font-medium text-[#737373] uppercase">Drop-off</span>
            </div>
          </div>
          <div className="space-y-0.5">
            {funnelData.map((step, idx) => (
              <FunnelStep 
                key={step.stage} 
                step={step} 
                maxCount={maxFunnelCount}
                isFirst={idx === 0}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ROW 3: Trend Lead + Andamento Economico */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Trend Lead */}
        <Card className="card-abitalo" data-testid="trend-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-[#0078C8]" />
              <span>Trend Lead</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      formatter={(value, name) => [value, name]}
                    />
                    <Area type="monotone" dataKey="created" name="Creati" stroke="#3B82F6" fill="#3B82F680" />
                    <Area type="monotone" dataKey="converted" name="Convertiti" stroke="#10B981" fill="#10B98180" />
                    <Area type="monotone" dataKey="lost" name="Persi" stroke="#EF4444" fill="#EF444480" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-[#737373] text-sm">
                Dati insufficienti per il periodo selezionato
              </div>
            )}
            <div className="flex items-center justify-center gap-4 mt-2 text-xs">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Creati: {leadsTrend?.total_created || 0}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> Convertiti: {leadsTrend?.total_converted || 0}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> Persi: {leadsTrend?.total_lost || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Andamento Economico */}
        <Card className="card-abitalo" data-testid="revenue-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Euro className="w-4 h-4 text-[#008A3D]" />
              <span>Andamento Economico</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {revenueData.length > 1 ? (
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
                    <YAxis tick={{ fontSize: 11 }} stroke="#9CA3AF" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Area type="monotone" dataKey="contracted" name="Contratti" stroke="#FF7819" fill="#FF781980" />
                    <Area type="monotone" dataKey="received" name="Incassato" stroke="#10B981" fill="#10B98180" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-24 flex items-center justify-center bg-gray-50 rounded-lg mb-3">
                <div className="text-center">
                  <AlertCircle className="w-5 h-5 text-[#737373] mx-auto mb-1" />
                  <p className="text-xs text-[#737373]">Dati storici insufficienti per il grafico</p>
                  <p className="text-[10px] text-[#9CA3AF]">Riepilogo disponibile sotto</p>
                </div>
              </div>
            )}
            {/* Summary Cards - sempre visibili */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 pt-3 border-t">
              <div className="text-center p-2 bg-orange-50 rounded-lg">
                <p className="text-[10px] text-[#737373] uppercase font-medium">Contratti</p>
                <p className="text-sm font-bold text-[#FF7819]">{formatCurrency(revenue?.total_contracted || 0)}</p>
              </div>
              <div className="text-center p-2 bg-green-50 rounded-lg">
                <p className="text-[10px] text-[#737373] uppercase font-medium">Incassato</p>
                <p className="text-sm font-bold text-[#008A3D]">{formatCurrency(revenue?.total_received || 0)}</p>
              </div>
              <div className="text-center p-2 bg-yellow-50 rounded-lg">
                <p className="text-[10px] text-[#737373] uppercase font-medium">Da Incassare</p>
                <p className="text-sm font-bold text-[#B38C00]">{formatCurrency((revenue?.total_contracted || 0) - (revenue?.total_received || 0))}</p>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded-lg">
                <p className="text-[10px] text-[#737373] uppercase font-medium">Tasso Incasso</p>
                <p className="text-sm font-bold text-[#0078C8]">{revenue?.collection_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 4: Performance Team */}
      <Card className="card-abitalo" data-testid="team-performance-section">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-[#FF7819]" />
              <span>Performance Team</span>
            </CardTitle>
            {/* Role Filter Tabs */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {[
                { value: 'all', label: 'Tutti' },
                { value: 'pm', label: 'PM' },
                { value: 'technician', label: 'Tecnici' },
                { value: 'furniture', label: 'Arredi' }
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleRoleFilterChange(opt.value)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                    roleFilter === opt.value 
                      ? 'bg-white text-[#0078C8] shadow-sm' 
                      : 'text-[#737373] hover:text-[#3C3C3C]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Value Managed Chart */}
          {teamData.length > 0 && (
            <div className="h-40 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={teamData.slice(0, 6)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} stroke="#9CA3AF" width={100} />
                  <Tooltip 
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(value) => formatCurrency(value)}
                  />
                  <Bar dataKey="value_managed" name="Valore Gestito" fill="#FF7819" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          
          {/* Team Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 text-xs font-medium text-[#737373]">Nome</th>
                  <th className="text-left py-2 px-2 text-xs font-medium text-[#737373]">Ruolo</th>
                  <th className="text-right py-2 px-1 text-xs font-medium text-[#737373]">Lead</th>
                  <th className="text-right py-2 px-1 text-xs font-medium text-[#737373]">Prev</th>
                  <th className="text-right py-2 px-1 text-xs font-medium text-[#737373]">Contr</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-[#737373]">Valore Gestito</th>
                  <th className="text-right py-2 px-1 text-xs font-medium text-[#737373]">Conv</th>
                  <th className="text-right py-2 px-2 text-xs font-medium text-[#737373]">Da Fatt</th>
                </tr>
              </thead>
              <tbody>
                {teamData.length > 0 ? teamData.map((member) => (
                  <tr key={member.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-2 font-medium text-[#3C3C3C]">{member.name}</td>
                    <td className="py-2 px-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        member.role === 'PM' ? 'bg-purple-100 text-purple-700' :
                        member.role === 'Tecnico' ? 'bg-blue-100 text-blue-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {member.role}
                      </span>
                    </td>
                    <td className="py-2 px-1 text-right">{member.leads_assigned}</td>
                    <td className="py-2 px-1 text-right">{member.estimates_count}</td>
                    <td className="py-2 px-1 text-right">{member.contracts_count}</td>
                    <td className="py-2 px-2 text-right font-semibold text-[#008A3D]">{formatCurrency(member.value_managed)}</td>
                    <td className="py-2 px-1 text-right">
                      <span className={member.conversion_rate > 0 ? 'text-[#0078C8] font-medium' : 'text-[#737373]'}>
                        {member.conversion_rate}%
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right text-[#B38C00]">{formatCurrency(member.to_invoice_value)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-[#737373]">
                      Nessun membro del team per il filtro selezionato
                    </td>
                  </tr>
                )}
              </tbody>
              {teamData.length > 0 && teamPerformance?.totals && (
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="py-2 px-2 text-[#3C3C3C]" colSpan={2}>Totale</td>
                    <td className="py-2 px-1 text-right">{teamPerformance.totals.leads}</td>
                    <td className="py-2 px-1 text-right">{teamPerformance.totals.estimates}</td>
                    <td className="py-2 px-1 text-right">{teamPerformance.totals.contracts}</td>
                    <td className="py-2 px-2 text-right text-[#008A3D]">{formatCurrency(teamPerformance.totals.value_managed)}</td>
                    <td className="py-2 px-1 text-right">-</td>
                    <td className="py-2 px-2 text-right text-[#B38C00]">{formatCurrency(teamPerformance.totals.to_invoice)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ROW 5: Progetti per Stato (Compatto) */}
      <Card className="card-abitalo" data-testid="projects-status-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#008A3D]" />
            <span>Progetti per Stato</span>
            <span className="text-sm font-normal text-[#737373] ml-1">({projectsStatus?.total_projects || 0} totali)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {projectsDist.length > 0 ? projectsDist.map((item) => (
              <div key={item.status} className="bg-gray-50 rounded-lg p-3 text-center">
                <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ backgroundColor: item.color }} />
                <div className="text-lg font-bold text-[#3C3C3C]">{item.count}</div>
                <div className="text-[10px] text-[#737373] leading-tight">{item.label}</div>
              </div>
            )) : (
              <div className="col-span-full text-center py-4 text-[#737373]">
                Nessun progetto
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Note sui dati mancanti */}
      <div className="text-xs text-[#737373] bg-gray-50 rounded-lg p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Note sui dati:</strong> I tempi medi di conversione e l'analisi canali acquisizione richiedono dati storici attualmente non disponibili (status_history, source normalizzato). 
          Queste funzionalità saranno disponibili nella Fase 2 di Analytics.
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
